/**
 * useWatchedFixtures — watch/unwatch "no odds yet" fixtures (query_watchers collection).
 * Extracted verbatim from SgpFlowScreen.jsx (god-file decomposition).
 * Note: handleUnwatchFixture was unused in the screen before extraction; preserved for parity.
 */

import { useState } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp, getDocs, query, orderBy, where, deleteDoc } from 'firebase/firestore';

export default function useWatchedFixtures() {
    const [watchedFixtures, setWatchedFixtures] = useState(new Set()); // Set of fixture IDs being watched

    // Fetch watched fixtures for the current user
    const fetchWatchedFixtures = async () => {
        try {
            const u = auth?.currentUser;
            if (!u) return;

            const watchersQuery = query(
                collection(db, 'query_watchers'),
                where('uid', '==', u.uid),
                where('status', '==', 'active'),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(watchersQuery);
            const fixtureIds = new Set(snapshot.docs.map(doc => doc.data().fixtureId));
            setWatchedFixtures(fixtureIds);
            console.log('[fetchWatchedFixtures] Loaded', fixtureIds.size, 'watched fixtures');
        } catch (e) {
            console.error('[fetchWatchedFixtures] Error:', e);
        }
    };

    // Handle watching a "no odds yet" fixture
    const handleWatchFixture = async (fixtureOption) => {
        try {
            const u = auth?.currentUser;
            if (!u) {
                alert('Please sign in to watch fixtures');
                return;
            }

            const fixtureId = fixtureOption?._fixtureId || fixtureOption?.raw?.fixture_id || null;
            if (!fixtureId) {
                console.error('[handleWatchFixture] No fixture ID found');
                alert('Unable to watch this fixture. Please try again.');
                return;
            }

            // Check if already watching
            if (watchedFixtures.has(fixtureId)) {
                alert('You\'re already watching this fixture!');
                return;
            }

            const watcher = {
                uid: u.uid,
                fixtureId: fixtureId,
                fixtureTitle: fixtureOption.title || `${fixtureOption.away_team} vs ${fixtureOption.home_team}`,
                homeTeam: fixtureOption.home_team || null,
                awayTeam: fixtureOption.away_team || null,
                startDate: fixtureOption.start_date || fixtureOption.date || null,
                sport: fixtureOption.sport || 'basketball',
                league: fixtureOption.league || 'nba',
                status: 'active', // active | completed | expired
                createdAt: serverTimestamp(),
                lastChecked: serverTimestamp(),
                notificationSent: false
            };

            await addDoc(collection(db, 'query_watchers'), watcher);
            
            // Update local state
            setWatchedFixtures(prev => new Set([...prev, fixtureId]));
            
            alert('🔔 Watching! We\'ll notify you when odds are available.');
            console.log('[handleWatchFixture] Watcher created:', watcher);
        } catch (e) {
            console.error('[handleWatchFixture] Error:', e);
            alert('Failed to watch fixture. Please try again.');
        }
    };

    // Handle unwatching a fixture
    const handleUnwatchFixture = async (fixtureOption) => {
        try {
            const u = auth?.currentUser;
            if (!u) {
                alert('Please sign in');
                return;
            }

            const fixtureId = fixtureOption?._fixtureId || fixtureOption?.raw?.fixture_id || null;
            if (!fixtureId) {
                console.error('[handleUnwatchFixture] No fixture ID found');
                return;
            }

            // Find the watcher document
            const watchersQuery = query(
                collection(db, 'query_watchers'),
                where('uid', '==', u.uid),
                where('fixtureId', '==', fixtureId),
                where('status', '==', 'active')
            );

            const snapshot = await getDocs(watchersQuery);
            
            // Delete all matching watchers
            const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            // Update local state
            setWatchedFixtures(prev => {
                const newSet = new Set(prev);
                newSet.delete(fixtureId);
                return newSet;
            });

            alert('✅ Stopped watching this fixture.');
            console.log('[handleUnwatchFixture] Stopped watching:', fixtureId);
        } catch (e) {
            console.error('[handleUnwatchFixture] Error:', e);
            alert('Failed to stop watching. Please try again.');
        }
    };

    return { watchedFixtures, fetchWatchedFixtures, handleWatchFixture, handleUnwatchFixture };
}
